export default function Support() {
  const supportFeatures = [
    {
      icon: 'ri-24-hours-line',
      title: '24/7 Technical Support',
      description: 'Round-the-clock assistance whenever you need help with your website. Our team is always available to resolve any issues quickly.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Security & Updates',
      description: 'Regular security patches, software updates, and backups to keep your website safe and running smoothly.'
    },
    {
      icon: 'ri-speed-line',
      title: 'Performance Monitoring',
      description: 'Continuous monitoring of your website\'s speed, uptime, and performance with proactive optimization.'
    },
    {
      icon: 'ri-edit-line',
      title: 'Content Updates',
      description: 'Need to update text, images, or add new pages? We handle all content changes quickly and professionally.'
    },
    {
      icon: 'ri-bug-line',
      title: 'Bug Fixes',
      description: 'Fast resolution of any technical issues or bugs that may arise, ensuring your site stays functional.'
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Monthly Reports',
      description: 'Detailed analytics and performance reports showing traffic, rankings, and key metrics for your website.'
    }
  ];

  const plans = [
    {
      name: 'Essential',
      price: '$99',
      period: '/month',
      features: [
        'Technical support (business hours)',
        'Security updates',
        'Monthly backups',
        'Performance monitoring',
        '2 content updates/month',
        'Monthly analytics report'
      ],
      popular: false
    },
    {
      name: 'Professional',
      price: '$199',
      period: '/month',
      features: [
        '24/7 technical support',
        'Priority security updates',
        'Weekly backups',
        'Advanced performance optimization',
        '5 content updates/month',
        'Bi-weekly analytics reports',
        'SEO monitoring',
        'Quarterly strategy calls'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      price: '$399',
      period: '/month',
      features: [
        '24/7 priority support',
        'Real-time security monitoring',
        'Daily backups',
        'Premium performance optimization',
        'Unlimited content updates',
        'Weekly analytics reports',
        'Advanced SEO management',
        'Monthly strategy calls',
        'Dedicated account manager'
      ],
      popular: false
    }
  ];

  return (
    <section className="py-24 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-[#FF6B6B] text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-customer-service-line mr-2"></i>
            Ongoing Support
          </div>
          <h2 className="font-['Playfair_Display'] text-5xl font-bold text-[#1A2332] mb-4">
            We're Here When You Need Us
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Your website is a critical business tool. Our comprehensive support ensures it stays secure, fast, and effective at attracting clients.
          </p>
        </div>

        {/* Support Features */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {supportFeatures.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow"
            >
              <div className="w-12 h-12 bg-[#FF6B6B]/10 rounded-lg flex items-center justify-center mb-4">
                <i className={`${feature.icon} text-2xl text-[#FF6B6B]`}></i>
              </div>
              <h3 className="font-semibold text-lg text-[#1A2332] mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Support Plans */}
        <div className="mb-12">
          <h3 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A2332] text-center mb-12">
            Choose Your Support Plan
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 relative ${
                  plan.popular
                    ? 'border-2 border-[#FF6B6B] shadow-xl transform scale-105'
                    : 'border border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#FF6B6B] text-white px-4 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
                    Most Popular
                  </div>
                )}

                <div className="text-center mb-6">
                  <h4 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A2332] mb-2">
                    {plan.name}
                  </h4>
                  <div className="flex items-end justify-center gap-1">
                    <span className="text-5xl font-bold text-[#FF6B6B]">{plan.price}</span>
                    <span className="text-gray-600 mb-2">{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#FF6B6B] text-lg mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  className={`block text-center py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    plan.popular
                      ? 'bg-[#FF6B6B] text-white hover:bg-[#ff5252]'
                      : 'bg-[#1A2332] text-white hover:bg-[#2a3342]'
                  }`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Section */}
        <div className="bg-white rounded-2xl p-12 text-center">
          <h3 className="font-['Playfair_Display'] text-3xl font-bold text-[#1A2332] mb-6">
            Why Our Clients Trust Our Support
          </h3>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <div className="text-4xl font-bold text-[#FF6B6B] mb-2">&lt;2hrs</div>
              <div className="text-gray-600">Average Response Time</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#FF6B6B] mb-2">99.9%</div>
              <div className="text-gray-600">Uptime Guarantee</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#FF6B6B] mb-2">100%</div>
              <div className="text-gray-600">Client Retention Rate</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
